import { useEffect, useState } from "react";

/** Time each role holds before swapping. */
const HOLD_MS = 3200;
/** Fade-out window before the text is exchanged. */
const SWAP_MS = 260;

export type RoleRotation = { text: string; show: boolean };

/**
 * Cycles the "Currently" line.
 *
 * Under `prefers-reduced-motion` the rotation never starts and the first role
 * stays put — a moving line of text is exactly what that preference is for.
 */
export function useRoleRotation(roles: readonly string[]): RoleRotation {
  const [index, setIndex] = useState(0);
  const [show, setShow] = useState(true);

  useEffect(() => {
    if (roles.length < 2) return;
    if (typeof window === "undefined" || !window.matchMedia) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let swap: ReturnType<typeof setTimeout>;
    const timer = setInterval(() => {
      setShow(false);
      swap = setTimeout(() => {
        setIndex((i) => (i + 1) % roles.length);
        setShow(true);
      }, SWAP_MS);
    }, HOLD_MS);

    return () => {
      clearInterval(timer);
      clearTimeout(swap);
    };
  }, [roles.length]);

  return { text: roles[index] ?? roles[0] ?? "", show };
}
